import { useMemo, useState } from "react";
import { formatDate } from "../../utils/dateUtils";

const HealthRecordTable = ({ data }) => {
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(5);
  const [sortDir, setSortDir] = useState("desc");
  const [filterType, setFilterType] = useState("all");

  const filteredData = useMemo(() => {
    if (filterType === "all") return data;
    return data.filter(item => item.recordType === filterType);
  }, [data, filterType]);

  const sorted = useMemo(() => {
    return filteredData.slice().sort((a, b) => {
      if (a.date < b.date) return sortDir === "asc" ? -1 : 1;
      if (a.date > b.date) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortDir]);

  const pageCount = Math.ceil(sorted.length / rowsPerPage);
  const pageData = sorted.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const toggleSort = () => {
    setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    setPage(1);
  };

  const getRecordIcon = (recordType) => {
    const icons = {
      doctor: "👨‍⚕️",
      vaccination: "💉",
      test: "🧪",
      other: "📋"
    };
    return icons[recordType] || "📋";
  };

  const getRecordBadge = (recordType) => {
    const badges = {
      doctor: "badge bg-primary",
      vaccination: "badge bg-success",
      test: "badge bg-info",
      other: "badge bg-secondary"
    };
    return badges[recordType] || "badge bg-secondary";
  };

  return (
    <>
      <div className="mb-3">
        <select
          className="form-select form-select-sm"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          style={{ width: "200px" }}
        >
          <option value="all">All Health Records</option>
          <option value="doctor">Doctor Visits</option>
          <option value="vaccination">Vaccinations</option>
          <option value="test">Tests/Exams</option>
          <option value="other">Other</option>
        </select>
      </div>

      <div className="table-responsive">
        <table className="table table-sm table-striped table-hover">
          <thead className="table-light">
            <tr>
              <th scope="col" style={{ cursor: "pointer" }} onClick={toggleSort}>
                DateTime {sortDir === "desc" ? "▼" : "▲"}
              </th>
              <th scope="col">Type</th>
              <th scope="col">Title</th>
              <th scope="col">Notes</th>
            </tr>
          </thead>
          <tbody>
            {pageData.map((row, index) => (
              <tr key={index}>
                <td>{formatDate(row.date)}</td>
                <td>
                  <span className={`badge ${getRecordBadge(row.recordType)}`}>
                    {getRecordIcon(row.recordType)} {row.recordType}
                  </span>
                </td>
                <td><strong>{row.title}</strong></td>
                <td>{row.notes || <span className="text-muted">-</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pageCount > 1 && (
        <nav aria-label="Page navigation">
          <ul className="pagination pagination-sm">
            <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                disabled={page === 1}
              >
                Previous
              </button>
            </li>
            {[...Array(pageCount)].map((_, idx) => {
              const num = idx + 1;
              return (
                <li key={num} className={`page-item ${num === page ? "active" : ""}`}>
                  <button className="page-link" onClick={() => setPage(num)}>
                    {num}
                  </button>
                </li>
              );
            })}
            <li className={`page-item ${page === pageCount ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => setPage((prev) => Math.min(prev + 1, pageCount))}
                disabled={page === pageCount}
              >
                Next
              </button>
            </li>
          </ul>
        </nav>
      )}
    </>
  );
};

export default HealthRecordTable;
